export const SEASONS = [
  { id: "Childhood", label: "Childhood", note: "The early years", free: true },
  { id: "Teenage years", label: "Teenage years", note: "Growing up" },
  { id: "Young adult", label: "Young Adult", note: "Finding your way" },
  { id: "Midlife", label: "Midlife", note: "Building & becoming" },
  { id: "Retirement", label: "Retirement", note: "Looking back with peace" },
  { id: "Seniors", label: "Seniors", note: "Wisdom & reflection" },
  { id: "Other", label: "Others", note: "Anytime in between" },
];

export const FREE_SEASON = "Childhood";

export const CATEGORIES = [
  {
    id: "Love & Family",
    label: "Love & Family",
    color: "#B08D57",
    subtypes: ["Engagement", "Wedding", "Birth of a child", "Adoption", "Becoming a grandparent"],
  },
  {
    id: "Education & Growth",
    label: "Education & Growth",
    color: "#DDA77B",
    subtypes: [
      "First day of school", "High school graduation", "College / University graduation",
      "Returning to school later in life", "Receiving an award or recognition",
    ],
  },
  {
    id: "Work & Career",
    label: "Work & Career",
    color: "#6C849B",
    subtypes: ["First job", "New job", "Promotion", "Starting a business", "Retirement"],
  },
  {
    id: "Home & Living",
    label: "Home & Living",
    color: "#7EA193",
    subtypes: ["Moving house", "Buying a home", "Selling a home", "Major home renovation"],
  },
  {
    id: "Travel & Relocations",
    label: "Travel & Relocations",
    color: "#C99A5B",
    subtypes: [
      "First overseas trip", "Migration to another country", "Long vacation",
      "Vacation", "Weekend away", "Pilgrimage",
    ],
  },
  {
    id: "Health & Body",
    label: "Health & Body",
    color: "#8BA98C",
    subtypes: ["Major illness", "Recovery from illness", "Surgery"],
  },
  {
    id: "Loss & Difficult Times",
    label: "Loss & Difficult Times",
    color: "#8A7F76",
    subtypes: ["Death of a loved one", "Funeral", "Divorce", "Separation", "Hardship"],
  },
  {
    id: "Social & Community",
    label: "Social & Community",
    color: "#B58AA0",
    subtypes: ["Family reunion", "Important celebration (cultural / community)"],
  },
  {
    id: "Defining Moments",
    label: "Defining Moments",
    color: "#A66B5A",
    subtypes: ["A turning point", "A big decision", "A moment that changed everything"],
  },
  {
    id: "Joyful Moments",
    label: "Joyful Moments",
    color: "#E0A94C",
    subtypes: [
      "Small joy", "Funny moment", "Proud moment", "Peaceful day",
      "Simple pleasure", "Thankful moment", "Family bonding moment",
    ],
  },
  {
    id: "Challenging Moments",
    label: "Challenging Moments",
    color: "#7B8CA0",
    subtypes: [
      "A difficult day", "Unexpected problem", "A disappointment",
      "A moment of fear", "A work struggle", "Health or body challenge",
    ],
  },
  {
    id: "Growth & Learning Moments",
    label: "Growth & Learning Moments",
    color: "#6FA08C",
    subtypes: [
      "Personal achievement", "First time trying", "Learning moment",
      "Mistake realized", "Moment of courage", "A quiet reflection",
    ],
  },
  {
    id: "Other Meaningful Moment",
    label: "Other Meaningful Moment",
    color: "#9C8B76",
    subtypes: [
      "A kind act received", "A kind act given", "A surprise",
      "An ordinary but meaningful day", "A hobby or leisure time", "Embarrassing moment",
    ],
  },
];

export const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

// Top-level Type of Memory — two simple choices (no second dropdown).
export const TYPE_OF_MEMORY = [
  { id: "Life Event", label: "Life Event" },
  { id: "Life Moment", label: "Life Moment" },
];

// Two-step Reflections — first choose Personal Growth or Life Challenges,
// then "Choose from the List" shows the matching grouped options.
export const REFLECTIONS = {
  "Personal Growth": [
    {
      group: "🌱 Personal Growth & Character",
      items: [
        "Patience", "Humility", "Responsibility", "Self respect", "Perseverance", "Courage",
        "Independence", "Trusting myself", "Being brave", "Being disciplined", "Being flexible",
        "Being realistic", "Being prepared", "Being positive", "Living in the present",
      ],
    },
    {
      group: "❤️ Relationships & Social Life",
      items: [
        "Forgiveness", "Honesty", "Respect for others", "Kindness", "Compassion", "Asking for help",
        "Setting boundaries", "Listening more", "Being careful with words", "Not judging others",
        "Loving unconditionally", "Valuing family", "Valuing friendship", "Helping others",
      ],
    },
    {
      group: "🧠 Emotional & Mental Strength",
      items: [
        "Letting go", "Accepting change", "Accepting mistakes", "Learning from failure",
        "Not giving up", "Choosing peace", "Controlling anger", "Staying hopeful", "Staying calm",
        "Staying strong", "Enjoying simple things",
      ],
    },
    {
      group: "💼 Practical Life Skills",
      items: ["Thinking before acting", "Saving money", "Managing time better", "Choosing wisely", "Being content"],
    },
    {
      group: "🌼 Values & Life Meaning",
      items: ["Gratitude", "Faith", "Trust", "Being thankful", "Appreciating life"],
    },
  ],
  "Life Challenges": [
    {
      group: "💔 Relationships & Family",
      items: [
        "Poor communication", "Taking loved ones for granted", "Holding grudges too long",
        "Trusting the wrong person", "Not setting boundaries", "Staying in an unhealthy relationship",
        "Ignoring family needs", "Not saying sorry sooner", "Letting pride damage relationships",
        "Avoiding difficult conversations",
      ],
    },
    {
      group: "💼 Work & Career",
      items: [
        "Choosing the wrong job", "Leaving a job too quickly", "Staying too long in the wrong job",
        "Not asking for help at work", "Missing opportunities", "Poor work life balance",
        "Not standing up for myself", "Procrastinating important tasks", "Poor leadership decisions",
        "Burning out",
      ],
    },
    {
      group: "💰 Money & Practical Life",
      items: [
        "Poor financial decisions", "Spending beyond means", "Not saving early",
        "Taking unnecessary risks", "Trusting the wrong financial advice",
        "Ignoring financial warning signs", "Poor planning", "Living without a budget",
        "Not preparing for emergencies", "Delaying important decisions",
      ],
    },
    {
      group: "🧠 Personal & Emotional",
      items: [
        "Letting fear control decisions", "Low self confidence", "Comparing myself to others",
        "Ignoring my intuition", "Avoiding responsibility", "Overreacting emotionally",
        "Holding onto regret", "Not forgiving myself", "Poor self care", "Ignoring mental health",
      ],
    },
    {
      group: "🌱 Health & Lifestyle",
      items: [
        "Neglecting physical health", "Ignoring medical advice", "Poor eating habits",
        "Lack of exercise", "Overworking the body", "Not resting enough", "Unhealthy coping habits",
        "Delaying treatment", "Ignoring warning signs", "Not listening to my body",
      ],
    },
  ],
};

export const REFLECTION_TYPES = Object.keys(REFLECTIONS);

// Kept for backward compatibility with memories saved before the redesign.
export const FEELINGS = [
  "Proud", "Grateful", "Joyful", "Peaceful", "Hopeful", "Nervous",
  "Brave", "Sad", "Loved", "Relieved", "Excited", "Thankful",
];

export const GIFT_OCCASIONS = [
  { id: "birth", label: "Birth", icon: "Baby",
    message: "Welcome to the world, little one. Here is a place for your story, starting from your very first day. We'll fill it with love until you're ready to write it yourself." },
  { id: "holiday", label: "A portable journal while on holiday", icon: "Compass",
    message: "For every road, beach and sunset ahead. Capture the moments of your travels so you can relive them for years to come. Bon voyage!" },
  { id: "birthday", label: "Birthday", icon: "Cake",
    message: "Happy Birthday! Another year of wonderful memories. Here's a place to keep every chapter of your story, for life." },
  { id: "graduation", label: "Graduation", icon: "GraduationCap",
    message: "Congratulations, graduate! As one chapter closes and a new one begins, here's a place to record every step of the journey ahead." },
  { id: "wedding", label: "Wedding", icon: "Heart",
    message: "Congratulations on your wedding! May your life together be full of love and laughter. Here's a place to keep every memory you make together." },
  { id: "mothers-day", label: "Mother's Day", icon: "Flower",
    message: "Happy Mother's Day, Mum. Your stories, your love and your life lessons mean the world to us. Here's a place to keep them for always." },
  { id: "fathers-day", label: "Father's Day", icon: "Medal",
    message: "Happy Father's Day, Dad. Your stories and the lessons you've taught us deserve to be remembered. Here's a place to record them for the family." },
  { id: "retirement", label: "Retirement", icon: "Armchair",
    message: "Happy Retirement! After a lifetime of hard work, here's a place to look back on the journey and record the memories that matter most." },
];

// Pricing
export const PRICING = {
  currency: "A$",
  lifetime: {
    price: 23.99,
    label: "Lifetime Access",
    launch: true,
    tagline: "One payment. All Life Seasons. Keep recording your story for life.",
  },
  ai: [
    {
      id: "memory", key: "memory_insight", label: "Memory Insight", price: 1.99, perMemory: true,
      coverage: "Any one memory you choose — from any year or Life Season",
      desc: "A short, kind and meaningful insight of up to 300 words on one memory you choose — from any year or Life Season. Saved to your account with a PDF download.",
      note: "Open any memory and choose Memory Insight. Reread or download it anytime without paying again.",
    },
    {
      id: "yearly", key: "yearly_reflection", label: "Yearly Reflection", price: 8.99,
      coverage: "One selected calendar year (1 Jan – 31 Dec)",
      desc: "A longer, more detailed narrative of a chosen year's moments, lessons and personal growth. Saved permanently with PDF download.",
    },
    {
      id: "wholelife", key: "lifetime_reflection", label: "Whole Life Reflection", price: 36.99,
      coverage: "All memories saved up to the date it is generated",
      desc: "One complete reflection across every Life Season — turning points, values, important relationships, achievements, challenges and growth.",
      note: "One complete Reflection based on all memories saved at the time it is created. Future memories are not automatically added.",
    },
    {
      id: "ebook", key: "life_story_ebook", label: "My Life Story Ebook", price: 49.99,
      coverage: "A polished book created from selected memories",
      desc: "Your stories become an autobiographical book — cover, title page, dedication, contents, chapters and a closing page. Up to 3 optional photos are added privately on your own device.",
    },
  ],
  legacy: {
    label: "Legacy Edition",
    price: null,
    comingLater: true,
    desc: "A complete family keepsake bringing together your story, reflections and a message for your family. Price to be decided later.",
  },
};
